import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/App";
import usePageTitle from "@/hooks/usePageTitle";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertTriangle,
  Download,
  Printer,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Search,
  RefreshCw,
  CheckCircle,
  Clock,
  XCircle,
  BarChart3
} from "lucide-react";

const ErrorReconciliationReportPage = () => {
  usePageTitle("Error Reconciliation Report");
  const { token } = useAuth();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: 'total_cost', direction: 'desc' });
  const [statusFilter, setStatusFilter] = useState("all");
  const [errorTypeFilter, setErrorTypeFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("orders");

  // Get date range from URL params
  const urlParams = new URLSearchParams(window.location.search);
  const dateFrom = urlParams.get('from');
  const dateTo = urlParams.get('to');

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (statusFilter !== "all") params.append('status', statusFilter);
      if (errorTypeFilter !== "all") params.append('error_type', errorTypeFilter);
      
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/error-reconciliation${params.toString() ? `?${params}` : ''}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      } else {
        toast.error("Failed to fetch report");
      }
    } catch (error) {
      toast.error("Error loading report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [token, dateFrom, dateTo, statusFilter, errorTypeFilter]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1 text-amber-600" />
      : <ArrowDown className="w-3 h-3 ml-1 text-amber-600" />;
  };

  const filteredAndSortedOrders = useMemo(() => {
    if (!reportData?.orders) return [];
    
    let filtered = reportData.orders;
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(o => 
        o.tracking?.toLowerCase().includes(term) ||
        o.client_code?.toLowerCase().includes(term) ||
        o.product?.toLowerCase().includes(term) ||
        o.error_types?.some(e => e.toLowerCase().includes(term))
      );
    }
    
    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      const aVal = a[sortConfig.key] || 0;
      const bVal = b[sortConfig.key] || 0;
      
      if (typeof aVal === 'string') {
        return sortConfig.direction === 'asc' 
          ? (aVal || '').localeCompare(bVal || '')
          : (bVal || '').localeCompare(aVal || '');
      }
      
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    return sorted;
  }, [reportData, searchTerm, sortConfig]);

  const exportToCSV = () => {
    if (!reportData?.orders) return;
    
    const headers = ["Tracking", "Partner", "Order Date", "Product", "Service", "Country", "Total Cost", "Error Types", "Status"];
    const rows = filteredAndSortedOrders.map(o => [
      o.tracking,
      o.client_code,
      o.order_date,
      o.product,
      o.service,
      o.country,
      o.total_cost.toFixed(2),
      o.error_types.join("; "),
      o.is_resolved ? "Resolved" : "Pending"
    ]);
    
    const csvContent = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `error_reconciliation_report_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Report exported!");
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6 print:p-2">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Print Styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none !important; }
          .print-break { page-break-before: always; }
          table { font-size: 9px; }
          th, td { padding: 3px 5px !important; }
        }
      `}</style>

      {/* Header - Non-printable controls */}
      <div className="no-print sticky top-0 z-20 bg-white dark:bg-gray-800 border-b shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Error Reconciliation Report</h1>
                <p className="text-sm text-muted-foreground">
                  Generated: {reportData?.generated_at ? format(new Date(reportData.generated_at), 'dd MMM yyyy, HH:mm') : '-'}
                  {(dateFrom || dateTo) && (
                    <span className="ml-2">
                      | Period: {dateFrom || 'Start'} to {dateTo || 'Today'}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={fetchReport} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={exportToCSV}>
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Button size="sm" onClick={handlePrint} className="bg-amber-600 hover:bg-amber-700">
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Printable Content */}
      <div className="max-w-[1600px] mx-auto p-6 print:p-2 print:max-w-none">
        {/* Print Header */}
        <div className="hidden print:block mb-4">
          <h1 className="text-2xl font-bold text-center">Error Reconciliation Report</h1>
          <p className="text-center text-gray-600">
            Generated: {reportData?.generated_at ? format(new Date(reportData.generated_at), 'dd MMM yyyy, HH:mm') : '-'}
            {(dateFrom || dateTo) && ` | Period: ${dateFrom || 'Start'} to ${dateTo || 'Today'}`}
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-950/30 dark:to-yellow-950/30 border-amber-200">
            <CardContent className="p-4">
              <p className="text-xs font-medium text-amber-600 uppercase">Total Errors</p>
              <p className="text-2xl font-bold text-amber-700 mt-1">
                {reportData?.summary?.total_errors || 0}
              </p>
              <p className="text-xs text-amber-500">Orders with errors</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-950/30 dark:to-red-950/30 border-orange-200">
            <CardContent className="p-4">
              <p className="text-xs font-medium text-orange-600 uppercase">Pending</p>
              <p className="text-2xl font-bold text-orange-700 mt-1">
                {reportData?.summary?.pending || 0}
              </p>
              <p className="text-xs text-orange-500">Needs resolution</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 border-green-200">
            <CardContent className="p-4">
              <p className="text-xs font-medium text-green-600 uppercase">Resolved</p>
              <p className="text-2xl font-bold text-green-700 mt-1">
                {reportData?.summary?.resolved || 0}
              </p>
              <p className="text-xs text-green-500">{reportData?.summary?.resolution_rate || 0}% resolved</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <p className="text-xs font-medium text-muted-foreground uppercase">Affected Value</p>
              <p className="text-2xl font-bold mt-1">
                ₹{(reportData?.summary?.total_affected_value || 0).toLocaleString()}
              </p>
              <p className="text-xs text-muted-foreground">Total order value</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <p className="text-xs font-medium text-muted-foreground uppercase">Resolution Rate</p>
              <p className="text-2xl font-bold mt-1">{reportData?.summary?.resolution_rate || 0}%</p>
              <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-green-500 transition-all" 
                  style={{ width: `${reportData?.summary?.resolution_rate || 0}%` }}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="no-print">
          <TabsList className="mb-4">
            <TabsTrigger value="orders">Error Orders ({reportData?.order_count || 0})</TabsTrigger>
            <TabsTrigger value="types">Error Types ({reportData?.error_types?.length || 0})</TabsTrigger>
            <TabsTrigger value="clients">By Partner ({reportData?.client_summary?.length || 0})</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Filters - Non-printable */}
        <div className="no-print flex flex-wrap gap-4 mb-4">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search tracking, partner, product, error..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
            </SelectContent>
          </Select>
          <Select value={errorTypeFilter} onValueChange={setErrorTypeFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Error Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {reportData?.error_types?.map((et) => (
                <SelectItem key={et.type} value={et.type}>{et.type} ({et.count})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Error Orders Tab */}
        {activeTab === "orders" && (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-amber-50 dark:bg-amber-950/30">
                      <TableHead className="w-12 font-semibold">#</TableHead>
                      <TableHead 
                        className="font-semibold cursor-pointer hover:bg-amber-100"
                        onClick={() => handleSort('tracking')}
                      >
                        <div className="flex items-center">
                          Tracking
                          <SortIcon columnKey="tracking" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold cursor-pointer hover:bg-amber-100"
                        onClick={() => handleSort('client_code')}
                      >
                        <div className="flex items-center">
                          Partner
                          <SortIcon columnKey="client_code" />
                        </div>
                      </TableHead>
                      <TableHead className="font-semibold">Product</TableHead>
                      <TableHead 
                        className="font-semibold text-right cursor-pointer hover:bg-amber-100"
                        onClick={() => handleSort('total_cost')}
                      >
                        <div className="flex items-center justify-end">
                          Value
                          <SortIcon columnKey="total_cost" />
                        </div>
                      </TableHead>
                      <TableHead className="font-semibold">Error Types</TableHead>
                      <TableHead 
                        className="font-semibold text-center cursor-pointer hover:bg-amber-100"
                        onClick={() => handleSort('error_count')}
                      >
                        <div className="flex items-center justify-center">
                          Errors
                          <SortIcon columnKey="error_count" />
                        </div>
                      </TableHead>
                      <TableHead className="font-semibold text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAndSortedOrders.map((order, idx) => (
                      <TableRow 
                        key={order.tracking + idx} 
                        className={`hover:bg-gray-50 dark:hover:bg-gray-800 ${!order.is_resolved ? 'bg-red-50/30 dark:bg-red-950/10' : ''}`}
                      >
                        <TableCell className="text-muted-foreground">{idx + 1}</TableCell>
                        <TableCell className="font-mono text-sm">{order.tracking}</TableCell>
                        <TableCell className="font-medium">{order.client_code}</TableCell>
                        <TableCell className="max-w-[150px] truncate" title={order.product}>
                          {order.product || '-'}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          ₹{order.total_cost.toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {order.error_types.slice(0, 2).map((type, i) => (
                              <Badge key={i} variant="outline" className="text-xs text-amber-600 border-amber-300">
                                {type}
                              </Badge>
                            ))}
                            {order.error_types.length > 2 && (
                              <Badge variant="secondary" className="text-xs">
                                +{order.error_types.length - 2}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="destructive" className="text-xs">
                            {order.error_count}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          {order.is_resolved ? (
                            <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Resolved
                            </Badge>
                          ) : (
                            <Badge variant="destructive" className="text-xs">
                              <Clock className="w-3 h-3 mr-1" />
                              Pending
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    
                    {filteredAndSortedOrders.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                          <AlertTriangle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          No errors found matching your criteria
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Error Types Tab */}
        {activeTab === "types" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-amber-600" />
                Error Types Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {reportData?.error_types?.map((et, idx) => {
                  const percentage = reportData.summary.total_errors > 0 
                    ? Math.round((et.count / reportData.summary.total_errors) * 100) 
                    : 0;
                  return (
                    <div key={et.type} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{et.type}</span>
                        <span className="text-sm text-muted-foreground">{et.count} errors ({percentage}%)</span>
                      </div>
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-amber-500 transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* By Client Tab */}
        {activeTab === "clients" && (
          <Card>
            <CardHeader>
              <CardTitle>Top Partners by Error Count</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-amber-50 dark:bg-amber-950/30">
                    <TableHead className="font-semibold">#</TableHead>
                    <TableHead className="font-semibold">Partner Code</TableHead>
                    <TableHead className="font-semibold text-right">Error Count</TableHead>
                    <TableHead className="font-semibold text-right">Affected Value</TableHead>
                    <TableHead className="font-semibold">Distribution</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reportData?.client_summary?.map((client, idx) => {
                    const percentage = reportData.summary.total_errors > 0 
                      ? Math.round((client.error_count / reportData.summary.total_errors) * 100) 
                      : 0;
                    return (
                      <TableRow key={client.client_code} className="hover:bg-gray-50">
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell className="font-mono font-medium">{client.client_code}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant="destructive">{client.error_count}</Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          ₹{client.total_value.toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden max-w-[150px]">
                              <div 
                                className="h-full bg-amber-500"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                            <span className="text-xs text-muted-foreground w-10">{percentage}%</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Print Footer */}
        <div className="hidden print:block mt-4 pt-4 border-t text-center text-xs text-gray-500">
          <p>EAC - Error Reconciliation Report | Page 1</p>
        </div>
      </div>
    </div>
  );
};

export default ErrorReconciliationReportPage;
